import { LoadingContent, LoadingValue } from "@/components/LoadingContent";
import { OAuthPolicyReview } from "@/features/oauth/policy-review";
import { needsPolicyReview } from "@/features/oauth/api";
import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Alert, Button, Group, Stack, Text } from "@mantine/core";
import { CenteredCard } from "@/components/CenteredCard";
import { AccountChoice } from "@/components/AccountChoice";
import { useAppSession } from "@/features/auth/session";
import {
  getClient,
  decideConsent,
  providerRedirect,
  verifyOAuthRequest,
  needsReauthentication,
} from "@/features/oauth/api";
import { clearOAuthQuery, oauthRequestState } from "@/features/oauth/request";
import { ScopeDescriptions } from "@/features/oauth/scope-descriptions";
import { clientOrigin } from "@/features/oauth/scopes";
import { OAuthErrorDetails } from "@/features/oauth/error-details";

function ConsentPending({ description }: { description: string }) {
  return <CenteredCard title="Allow access to TripBears?" description={description}><Stack>
    <Text size="sm" c="dimmed">Client origin: <LoadingValue loading>{null}</LoadingValue></Text>
    <Text fw={500}>Requested permissions</Text>
    <LoadingContent label={description} variant="rows" />
    <Text size="sm" c="dimmed">You can disconnect in Connected apps. Expanding access requires a new authorization.</Text>
    <Group grow><Button disabled>Allow</Button><Button disabled variant="default">Deny</Button></Group>
  </Stack></CenteredCard>;
}
function ConsentPage() {
  const [request] = useState(() => oauthRequestState(window.location.search));
  const session = useAppSession();
  const verified = useQuery({
    queryKey: ["oauth-request", session.data?.user.id, request.query],
    queryFn: () => verifyOAuthRequest(request.query!),
    enabled: Boolean(request.query && session.data?.user),
    retry: false,
  });
  const details = verified.data;
  const client = useQuery({
    queryKey: ["oauth-client", session.data?.user.id, details?.clientId],
    queryFn: () => getClient(details!.clientId),
    enabled: Boolean(details && session.data?.user),
    retry: false,
  });
  const decision = useMutation({
    mutationFn: async (accept: boolean) => {
      if (!request.query) throw new Error("Missing authorization request.");
      const result = await decideConsent(request.query, accept);
      const target = providerRedirect(result.url);
      clearOAuthQuery();
      window.location.assign(target);
    },
  });
  if (request.error)
    return <CenteredCard title="Authorization unavailable" description={request.error}><OAuthErrorDetails error={request.failure} /></CenteredCard>;
  if (session.isPending)
    return <ConsentPending description="Checking your session…" />;
  const needsLogin =
    !session.data?.user ||
    [verified.error, client.error, decision.error].some(needsReauthentication);
  if (needsLogin)
    return (
      <CenteredCard
        title="Sign in to continue"
        description={
          session.error?.message ?? "Your authorization request has been kept in this tab."
        }
      >
        <OAuthErrorDetails error={[verified.error, client.error, decision.error].find(needsReauthentication) ?? session.error} />
        <Button component="a" href="/auth/sign-in?next=%2Foauth%2Fresume">
          Sign in
        </Button>
      </CenteredCard>
    );
  if (session.data?.user && [verified.error, client.error, decision.error].some(needsPolicyReview)) return <OAuthPolicyReview userId={session.data.user.id} onContinue={() => { decision.reset(); void verified.refetch(); void client.refetch(); }} />;
  if (verified.error)
    return <CenteredCard title="Authorization unavailable" description={verified.error.message}><OAuthErrorDetails error={verified.error} /></CenteredCard>;
  if (verified.isPending)
    return (
      <ConsentPending description="Verifying the authorization request…" />
    );
  return (
    <CenteredCard title="Allow access to TripBears?">
      <Stack>
        <AccountChoice email={session.data!.user.email} returnTo="/oauth/resume" />
        {client.isPending ? (
          <LoadingContent label="Loading client details…" rows={1} />
        ) : (
          <Text fw={600}>{client.data?.client_name ?? "MCP client"}</Text>
        )}
        <Text size="sm" c="dimmed">Client origin: {details ? clientOrigin(details.clientId) ?? "Unavailable for this client identity" : "Checking…"}</Text>
        {client.error ? (
          <Alert color="red">
            <Text size="sm">{client.error.message}</Text>
            <OAuthErrorDetails error={client.error} />
            <Button variant="subtle" onClick={() => void client.refetch()}>
              Retry
            </Button>
          </Alert>
        ) : null}
        <ScopeDescriptions scopes={details?.scopes ?? []} />
        <Text size="sm" c="dimmed">
          Only the permissions listed above are requested. Tool availability also depends on the
          service rollout. Each removal needs fresh approval in your assistant; a permission or
          tool annotation does not guarantee that the assistant will ask. You can disconnect in
          Connected apps. Expanding access requires a new authorization.
        </Text>
        {decision.error ? <Alert color="red"><Text size="sm">{decision.error.message}</Text><OAuthErrorDetails error={decision.error} /></Alert> : null}
        <Group grow>
          <Button
            disabled={!client.data || decision.isPending}
            loading={decision.isPending && decision.variables === true}
            onClick={() => decision.mutate(true)}
          >
            Allow
          </Button>
          <Button
            variant="default"
            disabled={decision.isPending}
            loading={decision.isPending && decision.variables === false}
            onClick={() => decision.mutate(false)}
          >
            Deny
          </Button>
        </Group>
      </Stack>
    </CenteredCard>
  );
}
export const Route = createFileRoute("/oauth/consent")({ component: ConsentPage });
